import { apiService } from '@/services/api'
import type { InventoryItem, InventoryResponse } from '@/types'
import { getBurnReward, formatRewardText, calculateTotalRewards } from '@/utils/rewards'
import { createIcons, Sparkles } from 'lucide'


export class InventoryManager {
  private userId: string
  private currentSort: 'rarity' | 'recent' = 'recent'
  private currentPage = 0
  private itemsPerPage = 100
  private totalItems = 0
  private selectedItems = new Set<string>()
  private isLoading = false
  private currentInventoryItems: InventoryItem[] = []
  private onBurnCallback?: () => Promise<void>
  private onListingChangeCallback?: () => Promise<void>
  
  // Modal-related properties
  private currentBurnMintId: string | null = null
  private currentListMintId: string | null = null
  private currentUnlistData: { listingId: string; mintId: string; price: number } | null = null
  private rarityBurnItems: InventoryItem[] | null = null


  private static readonly MAX_SPARKLES_PRICE = 100_000
  private static readonly SPARKLES_PER_STAR = 10

  constructor(
    userId: string,
    callbacks?: { onBurn?: () => Promise<void>; onListingChange?: () => Promise<void> }
  ) {
    this.userId = userId
    this.onBurnCallback = callbacks?.onBurn
    this.onListingChangeCallback = callbacks?.onListingChange
    this.setupEventListeners()
  }



  private setupEventListeners(): void {
    // Sort buttons
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      const sortBtn = target.closest('[data-sort]') as HTMLButtonElement
      if (sortBtn) {
        const sort = sortBtn.dataset.sort as 'rarity' | 'recent'
        this.changeSort(sort)
      }
    })

    // Pagination buttons
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.closest('#inventoryPrevPage')) {
        this.previousPage()
      } else if (target.closest('#inventoryNextPage')) {
        this.nextPage()
      }
    })

    // Select all checkbox
    document.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement
      if (target.id === 'selectAllInventory') {
        this.toggleSelectAll(target.checked)
      }
    })

    // Individual item checkboxes
    document.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement
      if (target.classList.contains('inventory-item-checkbox')) {
        const mintId = target.dataset.mintId
        if (mintId) {
          this.toggleItemSelection(mintId, target.checked)
        }
      }
    })

    // Batch delete buttons
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.closest('#deleteSelected')) {
        this.deleteSelected()
      } else if (target.closest('#deleteByRarity')) {
        this.showRarityDeleteModal()
      }
    })

    // Rarity delete modal
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.closest('#confirmRarityDelete')) {
        this.confirmRarityDelete()
      } else if (target.closest('#cancelRarityDelete')) {
        this.hideRarityDeleteModal()
      } else if (target.id === 'rarityDeleteModal') {
        // Only close when clicking the backdrop, not the modal content
        this.hideRarityDeleteModal()
      }
    })

    document.addEventListener('click', (e) => {
      const target = (e.target as HTMLElement).closest<HTMLButtonElement>('.inventory-marketplace-btn')
      if (!target) return

      const action = target.dataset.marketAction
      if (action === 'list') {
        const mintId = target.dataset.mintId
        if (mintId) {
          target.disabled = true
          this.listItemForSale(mintId).finally(() => {
            target.disabled = false
          })
        }
      } else if (action === 'unlist') {
        const listingId = target.dataset.listingId
        const mintId = target.dataset.mintId
        if (listingId && mintId) {
          target.disabled = true
          this.unlistItem(listingId, mintId).finally(() => {
            target.disabled = false
          })
        }
      } else if (action === 'burn') {
        const mintId = target.dataset.mintId
        if (mintId) {
          target.disabled = true
          this.burnSingleItem(mintId).finally(() => {
            target.disabled = false
          })
        }
      }
    })

    // Modal event listeners
    this.setupModalEventListeners()
  }

  async loadInventory(): Promise<void> {
    if (this.isLoading) return
    this.isLoading = true

    try {
      this.showLoadingSkeleton()
      const response = await apiService.getInventory(
        this.userId,
        this.itemsPerPage,
        this.currentPage * this.itemsPerPage,
        this.currentSort
      )
      this.totalItems = response.meta.total
      this.currentInventoryItems = response.items
      this.renderInventory(response)
      this.updatePagination()
      this.updateSortButtons()
    } catch (error) {
      this.showError((error as Error).message)
    } finally {
      this.isLoading = false
    }
  }

  private showLoadingSkeleton(): void {
    const inventoryList = document.getElementById('inventoryList')
    if (!inventoryList) return

    const skeletonItems = Array(this.itemsPerPage)
      .fill(0)
      .map(
        () => `
      <div class="inventory-item skeleton">
        <div class="fortune-card fortune-card--inventory skeleton">
          <div class="inventory-card-checkbox skeleton-checkbox"></div>
          <div class="fortune-card-header">
            <div class="skeleton-text skeleton-rarity"></div>
            <div class="skeleton-text skeleton-date"></div>
          </div>
          <div class="fortune-card-body">
            <div class="skeleton-text skeleton-message"></div>
          </div>
          <div class="fortune-card-footer fortune-card-footer--inventory">
            <div class="skeleton-button"></div>
            <div class="skeleton-button"></div>
          </div>
        </div>
      </div>
    `
      )
      .join('')

    inventoryList.innerHTML = skeletonItems
  }

  private renderInventory(response: InventoryResponse): void {
    const inventoryList = document.getElementById('inventoryList')
    const inventoryCount = document.getElementById('inventoryCount')
    const inventoryCountBadge = document.getElementById('inventoryCountBadge')
    
    if (!inventoryList) return

    if (response.items.length === 0) {
      inventoryList.innerHTML = `
        <div class="empty-inventory">
          <div class="empty-icon">🍪</div>
          <h3>No fortunes collected yet</h3>
          <p>Start cracking cookies to build your collection!</p>
        </div>
      `
      if (inventoryCount) inventoryCount.textContent = '0'
      if (inventoryCountBadge) inventoryCountBadge.textContent = '0'
      
      // Ensure selection UI is properly reset when inventory is empty
      this.updateSelectionUI()
      return
    }

    const itemsHtml = response.items.map(item => this.renderInventoryItem(item)).join('')
    inventoryList.innerHTML = itemsHtml

    if (inventoryCount) {
      inventoryCount.textContent = response.meta.total.toString()
    }
    if (inventoryCountBadge) {
      inventoryCountBadge.textContent = response.meta.total.toString()
    }

    this.updateSelectionUI()
  }

  private renderInventoryItem(item: InventoryItem): string {
    const listingPriceSparkles = item.listing_price_sparkles ?? null
    const isListed = Boolean(item.listing_id) && listingPriceSparkles
    
    if (isListed && this.selectedItems.has(item.mint_id)) {
      this.selectedItems.delete(item.mint_id)
    }

    const isSelected = !isListed && this.selectedItems.has(item.mint_id)
    const mintedDate = item.minted_at ? new Date(item.minted_at).toLocaleDateString() : ''
    const rarity = (item.rarity_tier || 'common').toLowerCase()
    const rarityClass = `rarity-${rarity}`
    const rarityBorderClass = `rarity-border-${rarity}`
    const stars = '★'.repeat(item.stars)
    const burnReward = getBurnReward(item.rarity_tier || '')
    const burnRewardDisplay = burnReward > 0 ? `+${burnReward}` : `${burnReward}`
    const listingDate = item.listing_created_at
      ? new Date(item.listing_created_at).toLocaleDateString()
      : null
    const listingPriceDisplay = isListed ? `${listingPriceSparkles!}<i data-lucide="sparkles"></i>` : ''

    const marketplaceHtml = isListed
      ? `
        <div class="fortune-card-meta inventory-listing-meta">
          <div class="listing-summary">
            <span class="listing-badge">Listed • ${listingPriceDisplay}</span>
            ${listingDate ? `<span class="listing-date">since ${listingDate}</span>` : ''}
          </div>
        </div>
        <div class="fortune-card-footer fortune-card-footer--inventory">
          <button
            class="inventory-marketplace-btn danger"
            data-market-action="unlist"
            data-listing-id="${item.listing_id || ''}"
            data-mint-id="${item.mint_id}"
          >
            Cancel Listing
          </button>
        </div>
      `
      : `
        <div class="fortune-card-footer fortune-card-footer--inventory">
          <button
            class="inventory-marketplace-btn"
            data-market-action="list"
            data-mint-id="${item.mint_id}"
          >
            List for Sale
          </button>
          <button
            class="inventory-marketplace-btn danger"
            data-market-action="burn"
            data-mint-id="${item.mint_id}"
          >
            Burn (${burnRewardDisplay})
          </button>
        </div>
      `

    const message = item.message?.trim() ? item.message : 'No message'
    const quotedMessage = `&ldquo;${this.escapeHtml(message)}&rdquo;`

    const selectTooltip = isListed ? 'Listed fortunes must be unlisted before burning.' : 'Select this fortune'

    return `
      <div class="inventory-item${isSelected ? ' is-selected' : ''}" data-mint-id="${item.mint_id}">
        <div class="fortune-card fortune-card--inventory ${rarityBorderClass}" data-listed="${isListed}">
          <div class="inventory-card-checkbox" ${isListed ? `title="${selectTooltip}"` : ''}>
            <input
              type="checkbox"
              class="inventory-item-checkbox"
              data-mint-id="${item.mint_id}"
              data-listed="${isListed}"
              aria-label="${selectTooltip}"
              ${isSelected ? 'checked' : ''}
              ${isListed ? 'disabled' : ''}
            >
          </div>
          <div class="fortune-card-header">
            <span class="rarity-badge ${rarityClass}">
              ${(item.rarity_tier || '').toUpperCase()} ${stars}
            </span>
            ${mintedDate ? `<span class="fortune-card-subtext">${mintedDate}</span>` : ''}
          </div>
          <div class="fortune-card-body">
            <p class="fortune-message">${quotedMessage}</p>
          </div>
          ${marketplaceHtml}
        </div>
      </div>
    `
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  private changeSort(sort: 'rarity' | 'recent'): void {
    if (this.currentSort === sort) return
    this.currentSort = sort
    this.currentPage = 0
    this.loadInventory()
  }

  private updateSortButtons(): void {
    document.querySelectorAll('[data-sort]').forEach(btn => {
      const button = btn as HTMLButtonElement
      const sort = button.dataset.sort
      button.classList.toggle('active', sort === this.currentSort)
    })
  }

  private previousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--
      this.loadInventory()
    }
  }

  private nextPage(): void {
    const maxPage = Math.ceil(this.totalItems / this.itemsPerPage) - 1
    if (this.currentPage < maxPage) {
      this.currentPage++
      this.loadInventory()
    }
  }

  private updatePagination(): void {
    const prevBtn = document.getElementById('inventoryPrevPage') as HTMLButtonElement
    const nextBtn = document.getElementById('inventoryNextPage') as HTMLButtonElement
    const pageInfo = document.getElementById('inventoryPageInfo')

    if (prevBtn) prevBtn.disabled = this.currentPage === 0
    
    const maxPage = Math.ceil(this.totalItems / this.itemsPerPage) - 1
    if (nextBtn) nextBtn.disabled = this.currentPage >= maxPage

    if (pageInfo) {
      const start = this.currentPage * this.itemsPerPage + 1
      const end = Math.min((this.currentPage + 1) * this.itemsPerPage, this.totalItems)
      pageInfo.textContent = `${start}-${end} of ${this.totalItems}`
    }
  }

  private toggleSelectAll(checked: boolean): void {
    const checkboxes = document.querySelectorAll('.inventory-item-checkbox') as NodeListOf<HTMLInputElement>
    checkboxes.forEach(checkbox => {
      const mintId = checkbox.dataset.mintId
      if (!mintId) return

      if (checkbox.disabled || checkbox.dataset.listed === 'true') {
        checkbox.checked = false
        this.selectedItems.delete(mintId)
        this.syncItemSelectionState(mintId, false)
        return
      }

      checkbox.checked = checked
      if (checked) {
        this.selectedItems.add(mintId)
      } else {
        this.selectedItems.delete(mintId)
      }
      this.syncItemSelectionState(mintId, checked)
    })
    this.updateSelectionUI()
  }

  private toggleItemSelection(mintId: string, checked: boolean): void {
    if (checked) {
      this.selectedItems.add(mintId)
    } else {
      this.selectedItems.delete(mintId)
    }
    this.syncItemSelectionState(mintId, checked)
    this.updateSelectionUI()
  }

  private syncItemSelectionState(mintId: string, isSelected: boolean): void {
    const itemEl = document.querySelector(`.inventory-item[data-mint-id="${mintId}"]`)
    if (!itemEl) return
    itemEl.classList.toggle('is-selected', isSelected)
  }

  private updateSelectionUI(): void {
    const selectedCount = this.selectedItems.size
    const deleteSelectedBtn = document.getElementById('deleteSelected') as HTMLButtonElement
    const selectionCount = document.getElementById('selectionCount')
    const selectAllCheckbox = document.getElementById('selectAllInventory') as HTMLInputElement
    const inventoryPage = document.getElementById('page-inventory')

    if (deleteSelectedBtn) {
      deleteSelectedBtn.disabled = selectedCount === 0
      deleteSelectedBtn.textContent = `Burn Selected (${selectedCount})`
    }

    if (selectionCount) {
      selectionCount.textContent = selectedCount.toString()
    }

    if (selectAllCheckbox) {
      const checkboxNodes = Array.from(
        document.querySelectorAll('.inventory-item-checkbox')
      ) as HTMLInputElement[]
      const enabledCheckboxes = checkboxNodes.filter(cb => !cb.disabled && cb.dataset.listed !== 'true')
      const enabledSelected = enabledCheckboxes.filter(cb => cb.checked)

      selectAllCheckbox.checked =
        enabledSelected.length > 0 && enabledSelected.length === enabledCheckboxes.length
      selectAllCheckbox.indeterminate =
        enabledSelected.length > 0 && enabledSelected.length < enabledCheckboxes.length
      selectAllCheckbox.disabled = enabledCheckboxes.length === 0
    }

    // Toggle compact selection mode UI
    if (inventoryPage) {
      inventoryPage.classList.toggle('has-selection', selectedCount > 0)
    }
  }

  private async getSelectedItemRarities(): Promise<string[]> {
    // Use the actual inventory data instead of parsing DOM
    const rarities: string[] = []
    
    this.currentInventoryItems.forEach(item => {
      if (this.selectedItems.has(item.mint_id)) {
        rarities.push(item.rarity_tier)
      }
    })
    
    return rarities
  }

  private async deleteSelected(): Promise<void> {
    if (this.selectedItems.size === 0) return

    // Calculate total rewards for selected items
    const selectedRarities = await this.getSelectedItemRarities()
    const totalReward = calculateTotalRewards(selectedRarities)
    const rewardText = formatRewardText(totalReward)

    // Show batch burn confirmation modal
    this.showBatchBurnConfirmModal(this.selectedItems.size, rewardText)
  }

  private showBatchBurnConfirmModal(count: number, rewardText: string): void {
    const modal = document.getElementById('burnConfirmModal')
    const messageEl = document.getElementById('burnConfirmMessage')
    const rewardAmountEl = document.getElementById('burnRewardAmount')

    if (!modal || !messageEl || !rewardAmountEl) return

    messageEl.textContent = `Are you sure you want to burn ${count} selected fortune(s)?`
    rewardAmountEl.textContent = rewardText

    // Set a flag to indicate this is a batch burn
    this.currentBurnMintId = 'BATCH_BURN'

    modal.style.display = 'flex'
    document.body.style.overflow = 'hidden'
  }

  private async confirmBatchBurn(): Promise<void> {
    if (this.selectedItems.size === 0) return

    try {
      const deletePromises = Array.from(this.selectedItems).map(mintId => 
        apiService.burnFortune(this.userId, mintId)
      )
      
      await Promise.all(deletePromises)
      const burnedCount = deletePromises.length
      
      // Calculate rewards again for success message
      const selectedRarities = await this.getSelectedItemRarities()
      const totalReward = calculateTotalRewards(selectedRarities)
      const rewardText = formatRewardText(totalReward)
      
      this.selectedItems.clear()
      await this.loadInventory()
      
      // Ensure selection UI is updated even if inventory is now empty
      this.updateSelectionUI()
      
      // Refresh the main UI totals
      if (this.onBurnCallback) {
        await this.onBurnCallback()
      }
      
      // Show success message with rewards
      this.showSuccessMessage(`Successfully burned ${burnedCount} fortune(s) and earned ${rewardText}!`)
    } catch (error) {
      this.showError(`Failed to burn fortunes: ${(error as Error).message}`)
    } finally {
      this.hideBurnConfirmModal()
    }
  }

  private showRarityDeleteModal(): void {
    const modal = document.getElementById('rarityDeleteModal')
    if (modal) {
      modal.style.display = 'flex'
    }
  }

  private hideRarityDeleteModal(): void {
    const modal = document.getElementById('rarityDeleteModal')
    if (modal) {
      modal.style.display = 'none'
    }
  }

  private async confirmRarityDelete(): Promise<void> {
    const raritySelect = document.getElementById('rarityDeleteSelect') as HTMLSelectElement
    const selectedRarity = raritySelect?.value
    
    if (!selectedRarity) return

    this.hideRarityDeleteModal()

    try {
      // Get all items of the selected rarity
      const response = await apiService.getInventory(this.userId, 1000, 0, 'recent')
      const itemsToDelete = response.items.filter(item => 
        (item.rarity_tier || '').toLowerCase() === selectedRarity.toLowerCase()
      )

      if (itemsToDelete.length === 0) {
        this.showError(`No ${selectedRarity} fortunes found to burn.`)
        return
      }

      // Calculate total rewards
      const rarities = itemsToDelete.map(item => item.rarity_tier)
      const totalReward = calculateTotalRewards(rarities)
      const rewardText = formatRewardText(totalReward)

      // Show confirmation modal for rarity burn
      this.showRarityBurnConfirmModal(selectedRarity, itemsToDelete.length, rewardText, itemsToDelete)
    } catch (error) {
      this.showError(`Failed to burn fortunes: ${(error as Error).message}`)
    }
  }

  private showRarityBurnConfirmModal(rarity: string, count: number, rewardText: string, items: InventoryItem[]): void {
    const modal = document.getElementById('burnConfirmModal')
    const messageEl = document.getElementById('burnConfirmMessage')
    const rewardAmountEl = document.getElementById('burnRewardAmount')

    if (!modal || !messageEl || !rewardAmountEl) return

    messageEl.textContent = `Are you sure you want to burn all ${count} ${rarity} fortune(s)?`
    rewardAmountEl.textContent = rewardText

    // Store the items to burn in a special property
    this.currentBurnMintId = 'RARITY_BURN'
    this.rarityBurnItems = items

    modal.style.display = 'flex'
    document.body.style.overflow = 'hidden'
  }

  private async confirmRarityBurn(): Promise<void> {
    if (!this.rarityBurnItems || this.rarityBurnItems.length === 0) return

    try {
      const deletePromises = this.rarityBurnItems.map(item => 
        apiService.burnFortune(this.userId, item.mint_id)
      )
      
      await Promise.all(deletePromises)
      const burnedCount = deletePromises.length
      
      // Calculate rewards for success message
      const rarities = this.rarityBurnItems.map(item => item.rarity_tier)
      const totalReward = calculateTotalRewards(rarities)
      const rewardText = formatRewardText(totalReward)
      const rarity = this.rarityBurnItems[0]?.rarity_tier || 'unknown'
      
      await this.loadInventory()
      
      // Ensure selection UI is updated even if inventory is now empty
      this.updateSelectionUI()
      
      // Refresh the main UI totals
      if (this.onBurnCallback) {
        await this.onBurnCallback()
      }
      
      // Show success message with rewards
      this.showSuccessMessage(`Successfully burned ${burnedCount} ${rarity} fortune(s) and earned ${rewardText}!`)
    } catch (error) {
      this.showError(`Failed to burn fortunes: ${(error as Error).message}`)
    } finally {
      this.hideBurnConfirmModal()
      this.rarityBurnItems = null
    }
  }

  private showError(message: string): void {
    const inventoryList = document.getElementById('inventoryList')
    if (inventoryList) {
      inventoryList.innerHTML = `
        <div class="error-state">
          <div class="error-icon">⚠️</div>
          <h3>Failed to load inventory</h3>
          <p>${message}</p>
          <button onclick="window.dispatchEvent(new Event('inventoryPageShown'))" class="retry-btn">
            Try Again
          </button>
        </div>
      `
    }
  }

  private showSuccessMessage(message: string): void {
    // Create a temporary success message
    const successDiv = document.createElement('div')
    successDiv.className = 'success-message'
    successDiv.textContent = message
    successDiv.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #4CAF50;
      color: white;
      padding: 12px 24px;
      border-radius: 8px;
      z-index: 1000;
      font-weight: 500;
    `
    
    document.body.appendChild(successDiv)
    
    setTimeout(() => {
      successDiv.remove()
    }, 3000)
  }

  private setupModalEventListeners(): void {
    // Burn confirmation modal
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.closest('#confirmBurnSingle')) {
        this.confirmBurnSingle()
      } else if (target.closest('#cancelBurnConfirm')) {
        this.hideBurnConfirmModal()
      } else if (target.id === 'burnConfirmModal') {
        this.hideBurnConfirmModal()
      }
    })

    // Listing price modal
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.closest('#confirmListingPrice')) {
        this.confirmListingPrice()
      } else if (target.closest('#cancelListingPrice')) {
        this.hideListingPriceModal()
      } else if (target.id === 'listingPriceModal') {
        this.hideListingPriceModal()
      }
    })

    // Unlist confirmation modal
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.closest('#confirmUnlist')) {
        this.confirmUnlist()
      } else if (target.closest('#cancelUnlistConfirm')) {
        this.hideUnlistConfirmModal()
      } else if (target.id === 'unlistConfirmModal') {
        this.hideUnlistConfirmModal()
      }
    })

    // Handle Enter key in price input
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target && (e.target as HTMLElement).id === 'sparklesPriceInput') {
        this.confirmListingPrice()
      }
    })
  }

  private async burnSingleItem(mintId: string): Promise<void> {
    const item = this.currentInventoryItems.find(entry => entry.mint_id === mintId)
    if (!item) return

    this.currentBurnMintId = mintId
    this.showBurnConfirmModal(item)
  }

  private showBurnConfirmModal(item: InventoryItem): void {
    const modal = document.getElementById('burnConfirmModal')
    const messageEl = document.getElementById('burnConfirmMessage')
    const rewardAmountEl = document.getElementById('burnRewardAmount')

    if (!modal || !messageEl || !rewardAmountEl) return

    const burnReward = getBurnReward(item.rarity_tier || '')
    const rewardText = formatRewardText(burnReward)

    messageEl.textContent = `Are you sure you want to burn this ${(item.rarity_tier || '').toLowerCase()} fortune?`
    rewardAmountEl.textContent = rewardText

    modal.style.display = 'flex'
    document.body.style.overflow = 'hidden'
  }

  private hideBurnConfirmModal(): void {
    const modal = document.getElementById('burnConfirmModal')
    if (modal) {
      modal.style.display = 'none'
      document.body.style.overflow = ''
      this.currentBurnMintId = null
    }
  }

  private async confirmBurnSingle(): Promise<void> {
    if (!this.currentBurnMintId) return

    // Handle batch burn
    if (this.currentBurnMintId === 'BATCH_BURN') {
      await this.confirmBatchBurn()
      return
    }

    // Handle rarity burn
    if (this.currentBurnMintId === 'RARITY_BURN') {
      await this.confirmRarityBurn()
      return
    }

    try {
      await apiService.burnFortune(this.userId, this.currentBurnMintId)
      this.selectedItems.delete(this.currentBurnMintId)
      this.syncItemSelectionState(this.currentBurnMintId, false)
      await this.loadInventory()
      this.updateSelectionUI()

      if (this.onBurnCallback) {
        await this.onBurnCallback()
      }

      const item = this.currentInventoryItems.find(entry => entry.mint_id === this.currentBurnMintId)
      const burnReward = item ? getBurnReward(item.rarity_tier || '') : 1
      const rewardText = formatRewardText(burnReward)
      this.showSuccessMessage(`Fortune burned successfully! Earned ${rewardText}`)
    } catch (error) {
      this.showError(`Failed to burn fortune: ${(error as Error).message}`)
    } finally {
      this.hideBurnConfirmModal()
    }
  }

  private async listItemForSale(mintId: string): Promise<void> {
    const item = this.currentInventoryItems.find((entry) => entry.mint_id === mintId)
    if (!item) return

    this.currentListMintId = mintId
    this.showListingPriceModal(item)
  }

  private showListingPriceModal(item: InventoryItem): void {
    const modal = document.getElementById('listingPriceModal')
    const priceInput = document.getElementById('sparklesPriceInput') as HTMLInputElement
    const suggestedPriceEl = document.getElementById('sparklesSuggestedPriceAmount')
    const payoutEl = document.getElementById('sparklesPayoutAmount')

    if (!modal || !priceInput || !suggestedPriceEl || !payoutEl) return

    priceInput.max = String(InventoryManager.MAX_SPARKLES_PRICE)
    priceInput.min = '1'
    priceInput.step = '1'

    const burnReward = getBurnReward(item.rarity_tier || '')
    const baseSuggestedPriceStars = burnReward > 0 ? burnReward * 2 : 15
    const suggestedPriceSparkles = baseSuggestedPriceStars * InventoryManager.SPARKLES_PER_STAR
    
    priceInput.value = String(suggestedPriceSparkles)
    suggestedPriceEl.innerHTML = `${suggestedPriceSparkles}<i data-lucide="sparkles"></i>`
    payoutEl.innerHTML = `${suggestedPriceSparkles}<i data-lucide="sparkles"></i> instantly`
    
    // Initialize Lucide icons after adding HTML
    createIcons({ icons: { Sparkles } })

    // Remove existing event listener and add new one for real-time payout updates
    const existingListener = (priceInput as any)._payoutUpdateListener
    if (existingListener) {
      priceInput.removeEventListener('input', existingListener)
    }
    
    const newListener = () => {
      const price = Number(priceInput.value) || 0
      payoutEl.innerHTML = `${price}<i data-lucide="sparkles"></i> instantly`
      // Initialize Lucide icons after updating HTML
      createIcons({ icons: { Sparkles } })
    }
    
    priceInput.addEventListener('input', newListener)
    ;(priceInput as any)._payoutUpdateListener = newListener

    modal.style.display = 'flex'
    document.body.style.overflow = 'hidden'

    // Focus the input after a short delay to ensure modal is visible
    setTimeout(() => {
      priceInput.focus()
      priceInput.select()
    }, 100)
  }



  private hideListingPriceModal(): void {
    const modal = document.getElementById('listingPriceModal')
    if (modal) {
      modal.style.display = 'none'
      document.body.style.overflow = ''
      this.currentListMintId = null
    }
  }

  private async confirmListingPrice(): Promise<void> {
    if (!this.currentListMintId) return

    const priceInput = document.getElementById('sparklesPriceInput') as HTMLInputElement
    if (!priceInput) return

    const price = Number(priceInput.value)
    if (!Number.isFinite(price)) {
      this.showError('Please enter a valid price in sparkles.')
      return
    }

    if (price <= 0) {
      this.showError('Listing price must be greater than zero.')
      return
    }

    if (price > InventoryManager.MAX_SPARKLES_PRICE) {
      this.showError(`Listings cannot exceed ${InventoryManager.MAX_SPARKLES_PRICE} sparkles.`)
      return
    }

    try {
      const listing = await apiService.listFortuneForSparkles(this.userId, this.currentListMintId, price)
      this.selectedItems.delete(this.currentListMintId)
      this.syncItemSelectionState(this.currentListMintId, false)
      await this.loadInventory()
      this.showSuccessMessage(`Fortune listed for ${listing.price_sparkles || 0} sparkles! You'll receive the full amount when sold - no fees!`)
      if (this.onListingChangeCallback) {
        await this.onListingChangeCallback()
      }
      window.dispatchEvent(new Event('marketplaceListingsChanged'))
    } catch (error) {
      const errorMessage = (error as Error).message
      this.showError(`Failed to list fortune: ${errorMessage}`)
    } finally {
      this.hideListingPriceModal()
    }
  }



  private async unlistItem(
    listingId: string,
    mintId: string
  ): Promise<void> {
    const item = this.currentInventoryItems.find(entry => entry.mint_id === mintId)
    if (!item || !item.listing_price_sparkles) return

    this.currentUnlistData = { listingId, mintId, price: item.listing_price_sparkles }
    this.showUnlistConfirmModal(item.listing_price_sparkles)
  }

  private showUnlistConfirmModal(price: number): void {
    const modal = document.getElementById('unlistConfirmModal')
    const priceEl = document.getElementById('currentListingPrice')

    if (!modal || !priceEl) return

    priceEl.innerHTML = `${price}<i data-lucide="sparkles"></i>`
    
    // Initialize Lucide icons after adding HTML
    createIcons({ icons: { Sparkles } })
    
    modal.style.display = 'flex'
    document.body.style.overflow = 'hidden'
  }

  private hideUnlistConfirmModal(): void {
    const modal = document.getElementById('unlistConfirmModal')
    if (modal) {
      modal.style.display = 'none'
      document.body.style.overflow = ''
      this.currentUnlistData = null
    }
  }

  private async confirmUnlist(): Promise<void> {
    if (!this.currentUnlistData) return

    try {
      await apiService.cancelListing(this.userId, this.currentUnlistData.listingId)
      this.selectedItems.delete(this.currentUnlistData.mintId)
      this.syncItemSelectionState(this.currentUnlistData.mintId, false)
      await this.loadInventory()
      this.showSuccessMessage('Listing removed successfully.')
      if (this.onListingChangeCallback) {
        await this.onListingChangeCallback()
      }
      window.dispatchEvent(new Event('marketplaceListingsChanged'))
    } catch (error) {
      this.showError(`Failed to cancel listing: ${(error as Error).message}`)
    } finally {
      this.hideUnlistConfirmModal()
    }
  }
}